/**
 * StreamColumn — unified real-time feed for dashboard V2
 *
 * Merges field activity + office events + mentions into one chronological
 * stream. Persists a "last visit" timestamp per user in localStorage and
 * shows a "X new since last visit" divider. Filter pills: All / Mine /
 * Field / Office.
 */

import { getLocaleSync } from '@/utils/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio,
  Sparkles,
  Siren,
  Flag,
  Clock,
  CheckCircle2,
  AtSign,
  TrendingUp,
  MessageSquare,
  Truck,
  ArrowUpRight,
  Package,
  PackageCheck,
} from 'lucide-react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import useTenantId, { tenantIdReadUnion } from '@/hooks/useTenantId';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { db } from '@/firebase';
import { useOfficeDashboard } from '@/hooks/useOfficeDashboard';
import { useMentions } from '@/hooks/useMentions';
import { useDeals } from '@/hooks/useDeals';
import type { LucideIcon } from 'lucide-react';
import type { ProvisioningItem } from '@/types/theo-workflow.types';

type StreamSource =
  | 'field'
  | 'office'
  | 'mention'
  | 'sales'
  | 'provisioning'        // Deal fully provisioned — surfaces in "All"
  | 'provisioning_detail';// Per-item status change — only in "Provisioning" filter
type Filter = 'all' | 'mine' | 'field' | 'office' | 'provisioning';

interface StreamItem {
  id: string;
  source: StreamSource;
  isMine: boolean;
  title: string;
  subtitle?: string;
  timestamp: string;          // ISO
  Icon: LucideIcon;
  iconBg: string;
  iconText: string;
  pulse?: boolean;
  onClick?: () => void;
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(getLocaleSync(), { hour: 'numeric', minute: '2-digit' });
}

/** Extract a stable ms timestamp from a string-or-Firestore-Timestamp field. */
function toMs(raw: unknown): number {
  if (!raw) return 0;
  if (typeof raw === 'string') return new Date(raw).getTime();
  const ms = (raw as { toMillis?: () => number }).toMillis?.();
  return typeof ms === 'number' ? ms : 0;
}

function provVerb(status: string): string {
  const s = status.toUpperCase();
  if (s === 'NOT_ORDERED')          return 'Awaiting order';
  if (s === 'QUOTE_REQUESTED')      return 'Quote requested';
  if (s === 'AWAITING_FUNDS')       return 'Awaiting funds';
  if (s === 'AWAITING_TECH_REQUEST') return 'Awaiting tech';
  if (s === 'ORDERED')              return 'Ordered';
  if (s === 'SHIPPED')              return 'Shipped';
  if (s === 'AT_OFFICE')            return 'Arrived at office';
  if (s === 'DELIVERED')            return 'Delivered';
  return status;
}

function getLastVisit(userId: string): number {
  if (!userId) return Date.now();
  try {
    const raw = localStorage.getItem(`dashboard_v2_last_visit_${userId}`);
    return raw ? parseInt(raw, 10) : Date.now() - 60 * 60_000; // default: 1 hr ago
  } catch {
    return Date.now() - 60 * 60_000;
  }
}

function saveLastVisit(userId: string) {
  if (!userId) return;
  try {
    localStorage.setItem(`dashboard_v2_last_visit_${userId}`, String(Date.now()));
  } catch {
    /* noop */
  }
}

export function StreamColumn() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id') || '';
  const userName = localStorage.getItem('logged_in_user') || '';
  const [filter, setFilter] = useState<Filter>('all');

  // Snapshot last-visit on first mount; save fresh timestamp 2s later
  const lastVisitRef = useRef<number>(getLastVisit(userId));
  useEffect(() => {
    const t = setTimeout(() => saveLastVisit(userId), 2000);
    return () => clearTimeout(t);
  }, [userId]);

  const { fieldActivity, feedItems, loading: dashboardLoading } = useOfficeDashboard();
  const { mentions, loading: mentionsLoading } = useMentions({ userId });
  const { deals } = useDeals();

  // Subscribe to ALL provisioning items — needed to detect "fully provisioned"
  // deals (we have to know every item's status, not just recent ones). The
  // 7-day windowing happens later when we build event objects.
  const [provisioningItems, setProvisioningItems] = useState<ProvisioningItem[]>([]);
  const tenantId = useTenantId();
  // Feature-pack gate: without the provisioning pack there is no
  // /home/provisioning to click through to, so the listener is skipped and the
  // provisioning events + filter chip drop out with it (an empty array is the
  // single lever — every downstream builder is driven off it).
  const hasProvisioning = useFeatureGate('provisioning');
  useEffect(() => {
    if (!hasProvisioning) {
      setProvisioningItems([]);
      return;
    }
    const q = query(
      collection(db, 'provisioning_items'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProvisioningItems(
          snap.docs.map((d) => ({ id: d.id, ...d.data() } as ProvisioningItem)),
        );
      },
      () => setProvisioningItems([]),
    );
    return unsub;
  }, [tenantId, hasProvisioning]);


  const items = useMemo<StreamItem[]>(() => {
    const list: StreamItem[] = [];

    // Field activity → primary source
    fieldActivity.forEach((a) => {
      const baseIcon = ((): { Icon: LucideIcon; iconBg: string; iconText: string; pulse?: boolean } => {
        switch (a.type) {
          case 'sos':       return { Icon: Siren, iconBg: 'bg-red-500/15', iconText: 'text-red-400', pulse: !a.acknowledged };
          case 'flagged':   return { Icon: Flag, iconBg: 'bg-amber-500/15', iconText: 'text-amber-400' };
          case 'request_more_time': return { Icon: Clock, iconBg: 'bg-orange-500/15', iconText: 'text-orange-400' };
          case 'completed': return { Icon: CheckCircle2, iconBg: 'bg-emerald-500/15', iconText: 'text-emerald-400' };
          case 'on_site':   return { Icon: Truck, iconBg: 'bg-blue-500/15', iconText: 'text-blue-400' };
          case 'scheduled': return { Icon: Clock, iconBg: 'bg-purple-500/15', iconText: 'text-purple-400' };
          default:          return { Icon: Radio, iconBg: 'bg-slate-500/10', iconText: 'text-slate-400' };
        }
      })();

      list.push({
        id: `field-${a.id}`,
        source: 'field',
        isMine: a.techName === userName,
        title: a.title,
        subtitle: a.subtitle,
        timestamp: a.timestamp,
        ...baseIcon,
        onClick: () => {
          if (a.dealId) navigate(`/home/deals/${a.dealId}`);
          else if (a.jobId) navigate(`/home/jobs?openId=${a.jobId}`);
        },
      });
    });

    // Office feed → urgent messages, high-priority requests, won deals
    feedItems.forEach((f) => {
      // timestamp may be an ISO string, a JS Date, or a Firestore Timestamp.
      const tsField = f.timestamp;
      const ts = typeof tsField === 'string' ? tsField
        : (tsField as { toDate?: () => Date }).toDate ? (tsField as { toDate(): Date }).toDate().toISOString()
        : new Date((tsField as Date) || Date.now()).toISOString();

      const cfg = ((): { Icon: LucideIcon; iconBg: string; iconText: string; source: StreamSource } => {
        if (f.type === 'tech_request') return { Icon: MessageSquare, iconBg: 'bg-purple-500/15', iconText: 'text-purple-400', source: 'office' };
        if (f.type === 'deal_won')     return { Icon: TrendingUp, iconBg: 'bg-emerald-500/15', iconText: 'text-emerald-400', source: 'sales' };
        return { Icon: Radio, iconBg: 'bg-slate-500/10', iconText: 'text-slate-400', source: 'office' };
      })();

      list.push({
        id: `feed-${f.id}`,
        source: cfg.source,
        isMine: false,
        title: f.title,
        subtitle: f.subtitle,
        timestamp: ts,
        Icon: cfg.Icon,
        iconBg: cfg.iconBg,
        iconText: cfg.iconText,
      });
    });

    // Mentions
    mentions.forEach((m) => {
      list.push({
        id: `mention-${m.id}`,
        source: 'mention',
        isMine: true, // mentions are always to me
        title: `${m.createdByName} mentioned you`,
        subtitle: m.entityTitle,
        timestamp: m.createdAt,
        Icon: AtSign,
        iconBg: 'bg-blue-500/15',
        iconText: 'text-blue-400',
        onClick: () => m.linkPath && navigate(m.linkPath),
      });
    });

    // ────────────────────────────────────────────────────────────────────
    // Provisioning events — split into two flavours
    // ────────────────────────────────────────────────────────────────────
    const SEVEN_DAYS_AGO = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const TERMINAL_STATUSES = new Set(['SHIPPED', 'AT_OFFICE', 'DELIVERED']);

    // Group ALL items by dealId so we can detect "fully provisioned" deals
    const itemsByDeal = new Map<string, ProvisioningItem[]>();
    provisioningItems.forEach((it) => {
      if (!it.dealId) return;
      const arr = itemsByDeal.get(it.dealId) || [];
      arr.push(it);
      itemsByDeal.set(it.dealId, arr);
    });

    // (a) Deal-level "Fully provisioned" events — show in main "All" feed
    itemsByDeal.forEach((dealItems, dealId) => {
      if (dealItems.length === 0) return;
      const allTerminal = dealItems.every((it) =>
        TERMINAL_STATUSES.has(String(it.status || '').toUpperCase()),
      );
      if (!allTerminal) return;

      const completionMs = Math.max(...dealItems.map((it) => toMs(it.statusChangedAt)));
      if (!Number.isFinite(completionMs) || completionMs < SEVEN_DAYS_AGO) return;

      const deal = deals.find((d) => d.id === dealId);
      const customerName = deal?.customerName || 'Deal';

      list.push({
        id: `prov-complete-${dealId}-${completionMs}`,
        source: 'provisioning',
        isMine: false,
        title: `Fully provisioned: ${customerName}`,
        subtitle: `${dealItems.length} item${dealItems.length === 1 ? '' : 's'} ready · ready to schedule`,
        timestamp: new Date(completionMs).toISOString(),
        Icon: PackageCheck,
        iconBg: 'bg-emerald-500/15',
        iconText: 'text-emerald-400',
        onClick: () => navigate('/home/provisioning'),
      });
    });

    // (b) Per-status grouped events — only visible under the "Provisioning"
    //     filter. Same group-by-deal+status+day pattern as before.
    interface ProvGroup {
      dealId: string;
      status: string;
      latestTs: string;
      itemCount: number;
      sampleItem?: string;
    }
    const provGroups = new Map<string, ProvGroup>();
    provisioningItems.forEach((it) => {
      const ms = toMs(it.statusChangedAt);
      if (ms < SEVEN_DAYS_AGO || !it.dealId || !it.status) return;
      const tsIso = new Date(ms).toISOString();
      const day = tsIso.slice(0, 10);
      const key = `${it.dealId}|${it.status}|${day}`;
      const existing = provGroups.get(key);
      if (!existing) {
        provGroups.set(key, {
          dealId: it.dealId,
          status: String(it.status),
          latestTs: tsIso,
          itemCount: 1,
          sampleItem: it.itemName,
        });
      } else {
        existing.itemCount += 1;
        if (tsIso > existing.latestTs) existing.latestTs = tsIso;
      }
    });
    provGroups.forEach((g, key) => {
      const deal = deals.find((d) => d.id === g.dealId);
      const customerName = deal?.customerName || 'Deal';
      const verb = provVerb(g.status);
      const titleSuffix = g.itemCount === 1
        ? (g.sampleItem || 'item')
        : `${g.itemCount} items`;
      list.push({
        id: `prov-detail-${key}`,
        source: 'provisioning_detail',
        isMine: false,
        title: `${verb}: ${titleSuffix}`,
        subtitle: `${customerName} · provisioning`,
        timestamp: g.latestTs,
        Icon: Package,
        iconBg: 'bg-purple-500/15',
        iconText: 'text-purple-400',
        onClick: () => navigate('/home/provisioning'),
      });
    });

    return list.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [fieldActivity, feedItems, mentions, provisioningItems, deals, userName, navigate]);

  // Filter
  // - All: everything EXCEPT per-item provisioning detail (those clutter)
  // - Provisioning: BOTH the deal-level and per-item events
  // - Mine/Field/Office: as before, no provisioning at all
  const filtered = useMemo(() => {
    if (filter === 'mine')         return items.filter((i) => i.isMine);
    if (filter === 'field')        return items.filter((i) => i.source === 'field');
    if (filter === 'office')       return items.filter((i) => i.source === 'office' || i.source === 'sales' || i.source === 'mention');
    if (filter === 'provisioning') return items.filter((i) => i.source === 'provisioning' || i.source === 'provisioning_detail');
    return items.filter((i) => i.source !== 'provisioning_detail');
  }, [items, filter]);

  // Split into "new since last visit" and "earlier"
  const lastVisit = lastVisitRef.current;
  const { newItems, earlierItems } = useMemo(() => {
    const newer: StreamItem[] = [];
    const older: StreamItem[] = [];
    filtered.forEach((it) => {
      const t = new Date(it.timestamp).getTime();
      if (t > lastVisit) newer.push(it);
      else older.push(it);
    });
    return { newItems: newer, earlierItems: older };
  }, [filtered, lastVisit]);

  const newCount = newItems.length;
  const totalCount = filtered.length;
  const lastVisitLabel = useMemo(() => formatTime(new Date(lastVisit).toISOString()), [lastVisit]);

  return (
    <section className="relative rounded-3xl border border-theme bg-surface-card backdrop-blur-xl flex flex-col h-[640px] max-h-[640px] shadow-[0_8px_32px_-12px_rgba(0,0,0,0.4)] overflow-hidden">
      {/* Apple glass top highlight */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none z-10" />
      {/* Header */}
      <header className="px-5 py-4 border-b border-theme-soft">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="relative w-8 h-8 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Radio className="w-4 h-4 text-white" strokeWidth={2.5} />
              <motion.span
                className="absolute -inset-1 rounded-xl border border-blue-400/40"
                animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <div>
              <h3 className="text-base font-bold text-theme-primary leading-tight flex items-center gap-2">
                Live Stream
                {newCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500 text-white shadow-md"
                  >
                    {newCount}
                  </motion.span>
                )}
              </h3>
              <p className="text-[11px] text-theme-muted leading-tight mt-0.5">
                {totalCount === 0 ? 'Quiet here' : `${totalCount} event${totalCount === 1 ? '' : 's'} today`}
              </p>
            </div>
          </div>

          {/* Filter pills */}
          <div className="flex gap-1 bg-surface-raised rounded-xl p-1">
            {((hasProvisioning
              ? ['all', 'mine', 'field', 'office', 'provisioning']
              : ['all', 'mine', 'field', 'office']) as Filter[]).map((f) => {
              const active = filter === f;
              const label = f === 'provisioning' ? 'Provisioning' : f;
              const activeBg = f === 'provisioning'
                ? 'bg-purple-500 text-white shadow-md'
                : 'bg-blue-500 text-white shadow-md';
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-lg transition-all capitalize ${
                    active ? activeBg : 'text-theme-muted hover:text-theme-secondary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Stream list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-0">
        {(dashboardLoading || mentionsLoading) && totalCount === 0 ? (
          <SkeletonStreamRows />
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Sparkles className="w-6 h-6 text-theme-muted/40" />
            <p className="text-xs text-theme-muted">Quiet here. Activity will stream in as it happens.</p>
          </div>
        ) : (
          <>
            {newCount > 0 && (
              <div className="flex items-center gap-2 my-1.5 px-1">
                <motion.span
                  className="w-2 h-2 rounded-full bg-blue-400"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-[10px] font-medium text-blue-400 uppercase tracking-wider">
                  {newCount} new since last visit · {lastVisitLabel}
                </span>
                <div className="flex-1 h-px bg-blue-400/20" />
              </div>
            )}

            <AnimatePresence initial={false}>
              {newItems.map((it, i) => (
                <StreamRow key={it.id} item={it} index={i} isNew />
              ))}
            </AnimatePresence>

            {earlierItems.length > 0 && newCount > 0 && (
              <div className="flex items-center gap-2 mt-3 mb-1.5 px-1">
                <span className="text-[10px] font-medium text-theme-muted/60 uppercase tracking-wider">Earlier</span>
                <div className="flex-1 h-px bg-theme-soft" />
              </div>
            )}

            {earlierItems.map((it, i) => (
              <StreamRow key={it.id} item={it} index={i} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function SkeletonStreamRows() {
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="rounded-lg p-2 flex items-start gap-2.5"
          style={{ opacity: 1 - i * 0.12 }}
        >
          <div className="w-7 h-7 rounded-lg bg-surface-hover shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5 pt-0.5">
            <div className="h-3 bg-surface-hover rounded w-2/3" />
            <div className="h-2.5 bg-surface-hover/70 rounded w-1/2" />
          </div>
          <div className="w-6 h-2.5 rounded bg-surface-hover/60 shrink-0 mt-1" />
        </div>
      ))}
    </div>
  );
}

function StreamRow({ item, index, isNew }: { item: StreamItem; index: number; isNew?: boolean }) {
  const Icon = item.Icon;
  return (
    <motion.button
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.2), duration: 0.18 }}
      onClick={item.onClick}
      className={`w-full text-left rounded-lg p-2 flex items-start gap-2.5 group transition-colors ${
        isNew ? 'hover:bg-blue-500/[0.06]' : 'hover:bg-surface-hover'
      }`}
    >
      <div className={`w-7 h-7 rounded-lg ${item.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
        {item.pulse ? (
          <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
            <Icon className={`w-3.5 h-3.5 ${item.iconText}`} />
          </motion.div>
        ) : (
          <Icon className={`w-3.5 h-3.5 ${item.iconText}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs truncate ${isNew ? 'font-medium text-theme-primary' : 'text-theme-secondary'}`}>
          {item.title}
        </p>
        {item.subtitle && (
          <p className="text-[10px] text-theme-muted truncate">{item.subtitle}</p>
        )}
      </div>
      <div className="flex flex-col items-end shrink-0 gap-0.5">
        <span className="text-[9px] text-theme-muted">{relTime(item.timestamp)}</span>
        {item.onClick && (
          <ArrowUpRight className="w-3 h-3 text-transparent group-hover:text-theme-muted transition-colors" />
        )}
      </div>
    </motion.button>
  );
}
